import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, FieldError, useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router'
import { $TOWNS } from '@components/$TOWNS/$TOWNS'
import { DepositInfo, useStakingDeposits } from '@components/ClaimRewardsPanel/hooks/useStakingData'
import { NodeOperatorCard } from '@components/DelegateToNodeOperatorPanel/operators/NodeOperatorCard'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { Panel } from '@components/Panel/Panel'
import { SpaceIcon } from '@components/SpaceIcon'
import { FadeInBox } from '@components/Transitions'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    Box,
    Button,
    ErrorMessage,
    FancyButton,
    Icon,
    Paragraph,
    PopupMenu,
    Stack,
    Text,
    TextField,
} from '@ui'
import { ethers } from 'ethers'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { Analytics } from 'hooks/useAnalytics'
import { StakableOperator, useFetchOperatorsQuery } from 'hooks/useOperatorData'
import { WalletReady } from 'privy/WalletReady'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { addressFromSpaceId } from 'ui/utils/utils'
import {
    NULL_ADDRESS,
    SpaceItem,
    useSpaceContext,
    useSpaceDelegationOperator,
    useStakingRewardsQuery,
    useTownsContext,
    useTownsTokenBalance,
} from 'use-towns-client'
import { formatUnits, getAddress, isAddress, parseUnits } from 'viem'
import { z } from 'zod'

import { NodeOperatorIcon } from '../NodeOperatorIcon/NodeOperatorIcon'

import { useHasMemberNftInAbstractAccount } from './hooks/useHasMemberNftInAbstractAccount'
import { useInitiateWithdraw } from './hooks/useInitiateWithdraw'
import {
    useClaimAndIncreaseStakeToTown,
    useClaimAndStakeToTown,
    useIncreaseStake,
    useStakeToTown,
} from './hooks/useStakeToTown'
import { StakeSpaceCard } from './StakeSpaceCard'
import { StakingProgressViz } from './StakingProgressViz'

const emptySpaceDeposits: DepositInfo[] = []

export const StakeToTownPanel = React.memo(() => {
    const [renderKey, setRenderKey] = useState(0)

    return <StakeToTownPanelInternal key={renderKey} setRenderKey={setRenderKey} />
})

export const StakeToTownPanelInternal = ({
    setRenderKey,
}: {
    setRenderKey: React.Dispatch<React.SetStateAction<number>>
}) => {
    // using search params to preselect a space from stake tab
    const [searchParams] = useSearchParams()
    const preselectedSpaceId = searchParams.get('spaceId')
    const isClaimAndStakeFlow = searchParams.get('stackId') === CHANNEL_INFO_PARAMS.CLAIM_REWARDS

    // Get current space context for defaulting dropdown
    const { spaceId: currentSpaceId } = useSpaceContext()

    // State for withdraw confirmation modal
    const [showWithdrawConfirmation, setShowWithdrawConfirmation] = useState(false)

    // grab current $TOWNS balance
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()

    const townsBalance =
        useTownsTokenBalance({
            address: abstractAccountAddress as `0x${string}`,
        })?.data ?? 0n

    // Get current rewards when in claim and stake flow
    const { data: currentReward } = useStakingRewardsQuery({
        beneficiary: abstractAccountAddress,
        enabled: !!abstractAccountAddress && isClaimAndStakeFlow,
    })

    // Convert reward amount to BigInt if from claim flow
    const rewardAmount = useMemo(() => {
        if (isClaimAndStakeFlow && currentReward) {
            return currentReward.toBigInt()
        }
        return 0n
    }, [isClaimAndStakeFlow, currentReward])

    // Use reward amount when from claim flow, otherwise wallet balance
    const availableBalance = isClaimAndStakeFlow ? rewardAmount : townsBalance
    const maxAmount = availableBalance

    const { submitIncreaseStake, isLoading: isIncreaseStakeTransacting } = useIncreaseStake()
    const {
        submitStake,
        isReady: isStakeWithDelegationReady,
        isLoading: isStakeWithDelegationTransacting,
    } = useStakeToTown()
    const { execute: submitClaimRewardsAndStakeToTown } = useClaimAndStakeToTown()
    const { execute: submitClaimRewardsAndIncreaseStakeToTown } = useClaimAndIncreaseStakeToTown()

    const schema = useMemo(
        () =>
            z.object({
                amount: z
                    .string()
                    .refine(
                        (val) =>
                            /^\d*\.?\d*$/.test(val) &&
                            parseUnits(val || '0', 18) >= parseUnits('0.000000000000000001', 18),
                        {
                            message: 'Please enter a valid amount',
                        },
                    )
                    .refine((val) => parseUnits(val || '0', 18) <= availableBalance, {
                        message: 'Amount cannot exceed current balance',
                    }),
                spaceId: z.custom<`0x${string}`>((val) => isAddress(val), {
                    message: 'Please select a Town',
                }),
                operatorAddress: z
                    .custom<`0x${string}`>((val) => isAddress(val), {
                        message: 'Please select a Node Operator',
                    })
                    .optional(),
            }),
        [availableBalance],
    )

    // Determine default space ID: preselected from URL params, then current space, then undefined
    const defaultSpaceId = useMemo(() => {
        if (preselectedSpaceId && isAddress(preselectedSpaceId)) {
            return preselectedSpaceId as `0x${string}`
        }
        if (currentSpaceId) {
            const address = addressFromSpaceId(currentSpaceId)
            return address ? getAddress(address) : undefined
        }
        return undefined
    }, [preselectedSpaceId, currentSpaceId])

    // Calculate default amount for form
    const defaultAmount = useMemo(() => {
        if (isClaimAndStakeFlow && rewardAmount > 0n) {
            return formatUnits(rewardAmount, 18)
        }
        return undefined
    }, [isClaimAndStakeFlow, rewardAmount])

    const {
        control,
        register,
        handleSubmit,
        watch,
        setValue,
        trigger,
        reset,
        formState: { isValid: isFormValid, errors },
    } = useForm<z.infer<typeof schema>>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: {
            spaceId: defaultSpaceId,
            amount: defaultAmount,
            operatorAddress: undefined,
        },
    })

    // Set the form value when defaultSpaceId is available
    useEffect(() => {
        if (defaultSpaceId) {
            setValue('spaceId', defaultSpaceId)
        }
    }, [defaultSpaceId, setValue])

    // Set the amount when coming from claim flow
    useEffect(() => {
        if (defaultAmount) {
            setValue('amount', defaultAmount)
        }
    }, [defaultAmount, setValue])

    const amount = watch('amount')
    const spaceId = watch('spaceId')
    const operatorAddress = watch('operatorAddress')

    const {
        data: hasMemberNftInAbstractAccount,
        isLoading: isLoadingHasMemberNftInAbstractAccount,
    } = useHasMemberNftInAbstractAccount({ spaceId })

    // check for existing stake deposit for selected space
    const {
        data: { groupedDeposits },
    } = useStakingDeposits({ owner: abstractAccountAddress })

    const existingStake = useMemo(
        () =>
            spaceId
                ? groupedDeposits?.find((s) => s.space.toLowerCase() === spaceId.toLowerCase())
                : undefined,
        [groupedDeposits, spaceId],
    )

    const spaceDeposits = useMemo(
        () => (existingStake ? existingStake.deposits : emptySpaceDeposits),
        [existingStake],
    )

    const existingDeposit = spaceDeposits.at(-1)

    // Check if space has delegation set
    const { data: currentDelegation, isLoading: isDelegationLoading } =
        useSpaceDelegationOperator(spaceId)
    const isDelegated = currentDelegation && currentDelegation !== NULL_ADDRESS

    // Only show operator selection if we're not loading and delegation is needed
    const isLoadingDelegationData =
        spaceId && (isDelegationLoading || currentDelegation === undefined)
    const needsOperatorSelection = spaceId && !isDelegated && !isLoadingDelegationData

    // Get operators data
    const { data: operatorsData } = useFetchOperatorsQuery()
    const operators = operatorsData?.operators || []

    const { openPanel } = usePanelActions()

    const onSubmit = useCallback(
        async (
            data: { amount: string; spaceId: `0x${string}`; operatorAddress?: `0x${string}` },
            getSigner: () => Promise<ethers.Signer | undefined>,
        ) => {
            const beneficiary = abstractAccountAddress

            if (!beneficiary) {
                console.error('[stake-to-town] requires address for beneficiary account')
                return
            }
            if (!getSigner) {
                console.error('[stake-to-town] Signer not available. Cannot submit stake.')
                return
            }

            const amount = parseUnits(data.amount, 18)
            const spaceId = data.spaceId
            const operatorAddress = data.operatorAddress

            if (!amount || !spaceId) {
                console.error(
                    '[stake-to-town] Required information for staking is missing (balance, or AA address).',
                )
                return
            }

            // Track submit stake analytics
            Analytics.getInstance().track('submit stake', {
                spaceID: spaceId,
                stakeAmount: Number(formatUnits(amount, 18)),
            })

            try {
                if (!existingDeposit) {
                    // Use stake with delegation for all new stakes
                    // The operation will handle whether to include delegation based on operatorAddress

                    if (operatorAddress) {
                        console.log('[stake-to-town] Using stake with delegation')
                    } else {
                        console.log('[stake-to-town] Using stake without delegation')
                    }

                    if (isClaimAndStakeFlow) {
                        await submitClaimRewardsAndStakeToTown({
                            amount,
                            delegatee: spaceId,
                            // spaceId,
                            operatorAddress,
                            beneficiary,
                            signer: await getSigner(),
                        })
                    } else {
                        await submitStake({
                            amount,
                            spaceId,
                            operatorAddress,
                            beneficiary,
                            getSigner,
                            onAlreadyDelegated: () => {
                                setRenderKey((prev) => prev + 1)
                            },
                        })
                    }
                } else {
                    // Use increaseStake if there's an existing deposit
                    console.log(
                        '[stake-to-town] Using increaseStake for existing deposit:',
                        existingDeposit.depositId,
                    )
                    if (isClaimAndStakeFlow) {
                        await submitClaimRewardsAndIncreaseStakeToTown({
                            depositId: BigInt(existingDeposit.depositId),
                            amount,
                            delegatee: spaceId,
                            beneficiary,
                            signer: await getSigner(),
                        })
                    } else {
                        await submitIncreaseStake({
                            depositId: BigInt(existingDeposit.depositId),
                            amount,
                            getSigner,
                        })
                    }
                }

                // Navigate back to trading wallet with staked tab and new deposit info
                const newDepositInfo = {
                    spaceId,
                    amount: amount.toString(),
                    isNewDeposit: !existingDeposit,
                    timestamp: Date.now(),
                }

                openPanel(CHANNEL_INFO_PARAMS.TRADING_WALLET, {
                    data: JSON.stringify({
                        activeTab: 'staked',
                        newDeposit: newDepositInfo,
                    }),
                })

                reset({ amount: '', spaceId: spaceId, operatorAddress: undefined })
            } catch (error) {
                console.error('[stake-to-town] Error in onSubmit during staking:', error)
            }
        },
        [
            abstractAccountAddress,
            existingDeposit,
            openPanel,
            reset,
            isClaimAndStakeFlow,
            submitStake,
            submitClaimRewardsAndStakeToTown,
            setRenderKey,
            submitClaimRewardsAndIncreaseStakeToTown,
            submitIncreaseStake,
        ],
    )

    const { spaces } = useTownsContext()

    const isTransacting = isIncreaseStakeTransacting || isStakeWithDelegationTransacting
    const canStake = needsOperatorSelection
        ? isStakeWithDelegationReady && operatorAddress && hasMemberNftInAbstractAccount?.isMember
        : isStakeWithDelegationReady
    const isDisabled = !canStake || !isFormValid || isTransacting || isLoadingDelegationData

    // Determine button text based on existing deposit
    const buttonText = existingDeposit ? 'Increase Stake' : 'Stake'

    const onWithdraw = useCallback(() => {
        setShowWithdrawConfirmation(true)
    }, [])

    const { initiateWithdraw } = useInitiateWithdraw()
    const handleWithdrawConfirm = useCallback(
        (getSigner: () => Promise<ethers.Signer | undefined>) => {
            console.log('[stake-to-town] confirmed withdraw for deposit:', existingDeposit)
            if (!existingDeposit) {
                console.error('[stake-to-town] No existing deposit found')
                return
            }

            setShowWithdrawConfirmation(false)

            initiateWithdraw({
                depositIds: spaceDeposits.map((d) => BigInt(d.depositId)),
                getSigner,
            })
        },
        [existingDeposit, initiateWithdraw, spaceDeposits],
    )

    const handleWithdrawCancel = useCallback(() => {
        setShowWithdrawConfirmation(false)
    }, [])

    const { data: stakingDeposits } = useStakingDeposits({ owner: abstractAccountAddress })

    const totalStakeValue = useMemo(() => {
        const amountNumber = Number(amount)
        const existingStakeValue = Number(
            ethers.utils.formatUnits(stakingDeposits?.totalStaked || 0n, 18),
        )
        return amountNumber + existingStakeValue
    }, [amount, stakingDeposits?.totalStaked])

    const showMembershipNftWarning = useMemo(() => {
        if (isLoadingHasMemberNftInAbstractAccount) {
            return false
        }
        return !hasMemberNftInAbstractAccount?.isMember
    }, [isLoadingHasMemberNftInAbstractAccount, hasMemberNftInAbstractAccount?.isMember])

    return (
        <>
            <Panel label="Stake to Town">
                <Stack gap="x4">
                    <StakingProgressViz value={totalStakeValue} />

                    {/* Stake to Town Section */}
                    <Stack gap="paragraph">
                        <Stack horizontal gap>
                            <Box grow>
                                <Paragraph fontWeight="strong">Stake to Town</Paragraph>
                            </Box>
                            {spaceDeposits.length > 0 && (
                                <>
                                    {!!existingDeposit?.depositId && (
                                        <RestakeButton depositId={existingDeposit.depositId} />
                                    )}
                                    <Box cursor="pointer" onClick={onWithdraw}>
                                        <Paragraph color="negative" fontWeight="medium">
                                            Withdraw{' '}
                                            {spaceDeposits.length > 1
                                                ? `(${spaceDeposits.length})`
                                                : ''}
                                        </Paragraph>
                                    </Box>
                                </>
                            )}
                        </Stack>
                        {!preselectedSpaceId && spaces?.length > 0 && (
                            <Controller
                                control={control}
                                defaultValue={undefined}
                                render={({ field }) => (
                                    <SpaceDropdown
                                        error={errors.spaceId}
                                        spaces={spaces}
                                        selected={field.value}
                                        onChange={(value) => {
                                            field.onChange(value)
                                        }}
                                    />
                                )}
                                name="spaceId"
                            />
                        )}
                        {errors.spaceId && <ErrorMessage errors={errors} fieldName="spaceId" />}

                        {spaceId && (
                            <StakeSpaceCard
                                spaceAddress={spaceId}
                                spaces={spaces}
                                amount={existingStake?.deposits.reduce(
                                    (acc, deposit) => acc + deposit.amount.toBigInt(),
                                    0n,
                                )}
                            />
                        )}
                    </Stack>

                    {/* Operator Selection Section */}
                    {needsOperatorSelection && !showMembershipNftWarning && (
                        <FadeInBox gap="paragraph">
                            <Stack gap="paragraph">
                                <Paragraph fontWeight="strong">Delegate to Node</Paragraph>
                                <Paragraph color="gray2">
                                    This town hasn&apos;t delegated to a node operator yet. Choose
                                    one:
                                </Paragraph>
                                <Controller
                                    control={control}
                                    defaultValue={undefined}
                                    render={({ field }) => (
                                        <NodeOperatorDropdown
                                            error={errors.operatorAddress}
                                            operators={operators}
                                            selected={field.value}
                                            onChange={(value) => {
                                                field.onChange(value)
                                            }}
                                        />
                                    )}
                                    name="operatorAddress"
                                />
                                {operatorAddress &&
                                    (() => {
                                        const selectedOperator = operators.find(
                                            (op) => op.address === operatorAddress,
                                        )
                                        return selectedOperator ? (
                                            <NodeOperatorCard
                                                operator={selectedOperator}
                                                operatorAddress={operatorAddress}
                                            />
                                        ) : null
                                    })()}
                                {errors.operatorAddress && (
                                    <ErrorMessage errors={errors} fieldName="operatorAddress" />
                                )}
                            </Stack>
                        </FadeInBox>
                    )}

                    {needsOperatorSelection && showMembershipNftWarning && (
                        <WarnAboutMembershipNft />
                    )}

                    {/* Amount Input Section */}
                    {isClaimAndStakeFlow ? (
                        <Stack gap>
                            <Paragraph fontWeight="strong">Amount</Paragraph>
                            <$TOWNS value={rewardAmount} />
                        </Stack>
                    ) : (
                        <Stack gap="paragraph">
                            <TextField
                                background="level2"
                                label={existingDeposit ? 'Increase Stake' : 'Enter Amount'}
                                renderLabel={(label) => (
                                    <Stack
                                        horizontal
                                        justifyContent="spaceBetween"
                                        alignItems="center"
                                    >
                                        <Box grow>
                                            <Paragraph fontWeight="strong">{label}</Paragraph>
                                        </Box>
                                        {availableBalance > 0n && (
                                            <Box
                                                cursor="pointer"
                                                onClick={() => {
                                                    setValue('amount', formatUnits(maxAmount, 18))
                                                    trigger('amount')
                                                }}
                                            >
                                                <Paragraph color="cta2" fontWeight="strong">
                                                    MAX
                                                </Paragraph>
                                            </Box>
                                        )}
                                    </Stack>
                                )}
                                type="number"
                                placeholder="Enter amount"
                                max={Number(maxAmount)}
                                {...register('amount')}
                            />
                            <Box>
                                {errors.amount ? (
                                    <ErrorMessage errors={errors} fieldName="amount" />
                                ) : (
                                    <Paragraph color="gray2">
                                        Available balance: <$TOWNS value={availableBalance ?? 0n} />
                                    </Paragraph>
                                )}
                            </Box>
                        </Stack>
                    )}

                    {/* Stake Button Section */}
                    <Stack grow gap="paragraph" justifyContent="end">
                        <WalletReady>
                            {({ getSigner }) => (
                                <FancyButton
                                    noLayout
                                    data-testid="stake-button"
                                    cta={!isDisabled}
                                    borderRadius="full"
                                    disabled={isDisabled}
                                    onClick={handleSubmit((data) => onSubmit(data, getSigner))}
                                >
                                    {buttonText}
                                </FancyButton>
                            )}
                        </WalletReady>
                    </Stack>
                </Stack>
            </Panel>

            {/* Withdraw Confirmation Modal */}
            {showWithdrawConfirmation && existingDeposit && (
                <WithdrawConfirmationModal
                    spaceId={spaceId}
                    spaceDeposits={spaceDeposits}
                    onConfirm={handleWithdrawConfirm}
                    onCancel={handleWithdrawCancel}
                />
            )}
        </>
    )
}

const RestakeButton = (props: { depositId: bigint }) => {
    const { depositId } = props
    const { openPanel } = usePanelActions()

    const onRestake = useCallback(() => {
        if (depositId) {
            openPanel(CHANNEL_INFO_PARAMS.REDELEGATE_STAKE, {
                depositId: depositId.toString(),
            })
        }
    }, [openPanel, depositId])
    return (
        <Box cursor="pointer" onClick={onRestake}>
            <Paragraph color="cta2" fontWeight="medium">
                Change Town
            </Paragraph>
        </Box>
    )
}

export const SpaceDropdown = ({
    spaces,
    selected,
    onChange,
}: {
    spaces: SpaceItem[]
    selected: `0x${string}`
    error: FieldError | undefined
    onChange: (space: string) => void
}) => {
    const currentValue = useMemo(
        () => spaces.find((o) => o.address === selected),
        [spaces, selected],
    )

    return (
        <PopupMenu
            data-testid="space-popup-menu"
            options={spaces}
            padding="sm"
            scrollHeight={216}
            renderButton={(option) => (
                <Stack horizontal padding="xs" gap="md">
                    <Box
                        grow
                        justifyContent="center"
                        alignItems="start"
                        color={option ? 'gray1' : 'gray2'}
                    >
                        <Text>{option?.name ?? 'Select a Town'}</Text>
                    </Box>
                    <Box centerContent>
                        <Icon type="down" />
                    </Box>
                </Stack>
            )}
            renderOption={(option) => (
                <Stack horizontal gap="md">
                    <Box height="x3" width="x3">
                        <SpaceIcon
                            width="x3"
                            background="level3"
                            height="x3"
                            variant="thumbnail50"
                            spaceId={option?.streamId}
                            fadeIn={false}
                            firstLetterOfSpaceName=""
                        />
                    </Box>
                    <Box justifyContent="center">
                        <Paragraph>{option?.name}</Paragraph>
                    </Box>
                </Stack>
            )}
            value={currentValue}
            getKey={(option) => (option ? option.address : '')}
            onChange={(option) => onChange(option ? option.address : '')}
        />
    )
}

export const NodeOperatorDropdown = ({
    operators,
    selected,
    onChange,
}: {
    operators: StakableOperator[]
    selected: `0x${string}` | undefined
    error: FieldError | undefined
    onChange: (operator: string) => void
}) => {
    const currentValue = useMemo(
        () => operators.find((o) => o.address === selected),
        [operators, selected],
    )

    return (
        <PopupMenu
            data-testid="operator-popup-menu"
            options={operators}
            padding="sm"
            scrollHeight={216}
            renderButton={(option) => (
                <Stack horizontal padding="xs" gap="md">
                    <Box
                        grow
                        justifyContent="center"
                        alignItems="start"
                        color={option ? 'gray1' : 'gray2'}
                    >
                        <Text>{option?.name ?? 'Select a Node Operator'}</Text>
                    </Box>
                    <Box centerContent>
                        <Icon type="down" />
                    </Box>
                </Stack>
            )}
            renderOption={(option) => (
                <Stack horizontal gap="md">
                    <Box>
                        <NodeOperatorIcon nodeOperator={option} size="sm" />
                    </Box>
                    <Box justifyContent="center">
                        <Paragraph>{option?.name}</Paragraph>
                    </Box>
                </Stack>
            )}
            value={currentValue}
            getKey={(option) => (option ? option.address : '')}
            onChange={(option) => onChange(option ? option.address : '')}
        />
    )
}

const WithdrawConfirmationModal = ({
    spaceId,
    onConfirm,
    onCancel,
    spaceDeposits,
}: {
    spaceId: `0x${string}` | undefined
    onConfirm: (getSigner: () => Promise<ethers.Signer | undefined>) => void
    onCancel: () => void
    spaceDeposits: DepositInfo[]
}) => {
    const { data: currentDelegation } = useSpaceDelegationOperator(spaceId)
    const { data: operators } = useFetchOperatorsQuery()
    const operator = operators?.operators.find(
        (o) => o.address.toLowerCase() === currentDelegation?.toLowerCase(),
    )
    const yieldPercentage = operator?.estimatedApr
        ? `${(operator.estimatedApr * 100).toFixed(1)}%`
        : 'N/A'

    const [panelState, setPanelState] = useState<'step1' | 'step2'>('step1')

    const onContinue = useCallback(() => {
        setPanelState('step2')
    }, [])

    return panelState === 'step1' ? (
        <ModalContainer asSheet minWidth="400" padding="x4" key="withdraw-step1" onHide={onCancel}>
            <Stack gap="x4">
                <Stack gap="lg" alignItems="start" maxWidth="400">
                    <Text fontWeight="strong" size="lg">
                        Are you sure you want to withdraw?
                    </Text>

                    <Stack gap="paragraph" color="gray1">
                        <Text>You will lose access to the following:</Text>
                        <Stack horizontal gap="sm" alignItems="start">
                            <Box centerContent minWidth="x3" paddingTop="xxs">
                                <Icon type="close" color="negative" size="square_xs" />
                            </Box>
                            <Box paddingTop="xs">
                                <Paragraph>
                                    Your special badge that showcases your support could be at risk.
                                </Paragraph>
                            </Box>
                        </Stack>

                        <Stack horizontal gap="sm" alignItems="start">
                            <Box centerContent minWidth="x3" paddingTop="xxs">
                                <Icon type="close" color="negative" size="square_xs" />
                            </Box>
                            <Box paddingTop="xs">
                                <Paragraph>
                                    You will stop earning {yieldPercentage} yield.
                                </Paragraph>
                            </Box>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack horizontal gap minWidth="100%" justifyContent="end">
                    <Button tone="level2" rounded="full" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button tone="negative" rounded="full" onClick={onContinue}>
                        Continue to Withdraw
                    </Button>
                </Stack>
            </Stack>
        </ModalContainer>
    ) : (
        <ModalContainer asSheet minWidth="400" padding="x4" key="withdraw-step2" onHide={onCancel}>
            <Stack gap="x4">
                <Stack gap="lg" alignItems="start" maxWidth="400">
                    <Text fontWeight="strong" size="lg">
                        Initiate Withdraw
                    </Text>
                    <Stack gap="paragraph" color="gray1">
                        <Paragraph fontWeight="strong">Your Current Stake</Paragraph>
                        <Paragraph>
                            <$TOWNS
                                value={spaceDeposits.reduce(
                                    (acc, deposit) => acc.add(deposit.amount),
                                    ethers.BigNumber.from(0),
                                )}
                            />
                        </Paragraph>
                    </Stack>
                    <Paragraph>
                        Initiating withdraw will require a 30 day lock up period, after which you
                        can withdraw the tokens to your wallet. You will not be earning rewards
                        during the lock up period.
                    </Paragraph>
                </Stack>
                <Stack gap="x4" alignItems="start" maxWidth="400">
                    <Stack horizontal gap minWidth="100%" justifyContent="end">
                        <Button tone="level2" rounded="full" onClick={onCancel}>
                            Cancel
                        </Button>
                        <WalletReady>
                            {({ getSigner }) => (
                                <FancyButton
                                    cta
                                    borderRadius="full"
                                    onClick={() => onConfirm(getSigner)}
                                >
                                    Continue to Withdraw
                                </FancyButton>
                            )}
                        </WalletReady>
                    </Stack>
                </Stack>
            </Stack>
        </ModalContainer>
    )
}

export function WarnAboutMembershipNft() {
    return (
        <Text display="inline" size="sm" color="negative">
            The town owner hasn’t delegated to a node operator yet. Please choose another town or
            you may stake to an operator{' '}
            <Link to="https://www.towns.com/token" target="_blank" rel="noopener noreferrer">
                <Text
                    style={{ textDecoration: 'underline' }}
                    as="span"
                    display="inline"
                    color="negative"
                    size="sm"
                >
                    here
                </Text>
            </Link>
            .
        </Text>
    )
}
